import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HighlightTemplate, SlideLayoutProps } from '../types';
import {
  BodyContent,
  LEADING,
  PADDING,
  SlideText,
  TYPE,
  fitTitle,
  formatPageLabel,
  hasMath,
  hasNextSlide,
  shouldShowCounter,
  splitLastWord,
} from '../_shared';

// "Bold Brush" - black display caps + orange brush script accent on a
// graph-paper surface.

const SURFACE = 'bg-white';
const INK = 'text-black';
const ACCENT = '#EA6A2A';

const GRID_BG: React.CSSProperties = {
  backgroundImage: `
    linear-gradient(to right, rgba(0,0,0,0.06) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(0,0,0,0.06) 1px, transparent 1px)
  `,
  backgroundSize: '52px 52px',
};

const GRID_BG_SMALL: React.CSSProperties = {
  backgroundImage: GRID_BG.backgroundImage,
  backgroundSize: '20px 20px',
};

function NextArrow() {
  return (
    <div
      className='pointer-events-none flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-black md:h-20 md:w-20'
      style={{ backgroundColor: ACCENT }}
      aria-hidden='true'
    >
      <ArrowRight
        className='h-7 w-7 text-black md:h-9 md:w-9'
        strokeWidth={3.5}
      />
    </div>
  );
}

function PageCounter({ label }: { label: string }) {
  return (
    <span className={cn('font-bold text-black/80', TYPE.page)}>{label}</span>
  );
}

function StackedTitle({
  title,
  size,
}: {
  title: string;
  size: 'cover' | 'body';
}) {
  const sizeClass = fitTitle(title, size);
  // Maths renders whole, in the block face: splitting off the last word
  // could cut through the expression.
  if (hasMath(title)) {
    return (
      <span
        className={cn(
          'font-extrabold uppercase tracking-tight',
          INK,
          sizeClass,
          LEADING.display
        )}
      >
        <SlideText text={title} />
      </span>
    );
  }
  const { lead, tail } = splitLastWord(title);

  return (
    <div className='flex flex-col items-start gap-1'>
      {lead && (
        <span
          className={cn(
            'font-extrabold uppercase tracking-tight',
            INK,
            sizeClass,
            LEADING.display
          )}
        >
          {lead}
        </span>
      )}
      <span
        className={cn('font-script italic', sizeClass, LEADING.display)}
        style={{ color: ACCENT }}
      >
        {tail}
      </span>
    </div>
  );
}

function CoverLayout({ card, slideIndex, totalSlides }: SlideLayoutProps) {
  return (
    <div
      className={cn('relative flex h-full w-full flex-col', SURFACE)}
      style={GRID_BG}
    >
      <div
        className={cn(
          'flex flex-1 flex-col justify-center',
          PADDING.slideX,
          PADDING.slideTop
        )}
      >
        <StackedTitle title={card.title} size='cover' />
        {card.body && (
          <p
            className={cn(
              'mt-6 max-w-md text-balance text-black/80',
              TYPE.coverSubtitle,
              LEADING.body
            )}
          >
            <SlideText text={card.body} />
          </p>
        )}
      </div>

      <div
        className={cn(
          'flex items-center justify-end pb-8 md:pb-10',
          PADDING.slideX
        )}
      >
        {hasNextSlide(slideIndex, totalSlides) && <NextArrow />}
      </div>
    </div>
  );
}

function ContentLayout({ card, slideIndex, totalSlides }: SlideLayoutProps) {
  const showArrow = hasNextSlide(slideIndex, totalSlides);
  const showCounter = shouldShowCounter(slideIndex, totalSlides);

  return (
    <div
      className={cn('relative flex h-full w-full flex-col', SURFACE)}
      style={GRID_BG}
    >
      <div
        className={cn(
          'relative flex flex-1 flex-col justify-center',
          PADDING.slideX,
          PADDING.slideTop
        )}
      >
        <StackedTitle title={card.title} size='body' />

        {card.body && (
          <div className='mt-8 max-w-xl'>
            <BodyContent
              body={card.body}
              visual={card.visual}
              className={INK}
              spacing='space-y-4'
            />
          </div>
        )}
      </div>

      <div
        className={cn(
          'flex items-center justify-between gap-4 pb-8 md:pb-10',
          PADDING.slideX
        )}
      >
        <div>
          {showCounter && (
            <PageCounter label={formatPageLabel(slideIndex, totalSlides)} />
          )}
        </div>
        {showArrow && <NextArrow />}
      </div>
    </div>
  );
}

// Thumbnail title: block caps lead + script tail; maths renders whole.
function ThumbTitle({ title }: { title: string }) {
  const sizeClass = fitTitle(title, 'thumb');
  const blockClass = cn(
    'line-clamp-2 font-extrabold uppercase tracking-tight',
    INK,
    sizeClass,
    LEADING.display
  );
  if (hasMath(title)) {
    return (
      <span className={blockClass}>
        <SlideText text={title} />
      </span>
    );
  }
  const { lead, tail } = splitLastWord(title);
  return (
    <>
      {lead && <span className={blockClass}>{lead}</span>}
      <span
        className={cn(
          'line-clamp-1 font-script italic',
          sizeClass,
          LEADING.display
        )}
        style={{ color: ACCENT }}
      >
        {tail}
      </span>
    </>
  );
}

function ThumbnailLayout({ card, totalSlides }: SlideLayoutProps) {
  return (
    <div
      className={cn('relative flex h-full w-full flex-col', SURFACE)}
      style={GRID_BG_SMALL}
    >
      <div className='flex flex-1 flex-col justify-center px-5'>
        <ThumbTitle title={card.title} />
      </div>

      {totalSlides > 1 && (
        <div
          className='absolute bottom-3 right-3 flex h-7 w-7 items-center justify-center rounded-full border-2 border-black'
          style={{ backgroundColor: ACCENT }}
          aria-hidden='true'
        >
          <ArrowRight className='h-3.5 w-3.5 text-black' strokeWidth={3.5} />
        </div>
      )}
    </div>
  );
}

export const Template03: HighlightTemplate = {
  id: 'template-03',
  name: 'Bold Brush',
  layouts: {
    cover: CoverLayout,
    body: ContentLayout,
    summary: ContentLayout,
  },
  thumbnail: ThumbnailLayout,
};
